<template>
    <div class="border-b border-theme-border w-full mb-2">
        <div>
            <div class="w-full mb-2 flex justify-between items-center">
                <div
                    class="flex lg:flex-row flex-col items-start lg:items-center gap-2"
                >
                    <div class="mx-1">
                        <div
                            v-if="
                                enabled &&
                                referenceComponent.id &&
                                $enabledEcommerceOptions
                            "
                            class="flex items-center gap-2"
                        >
                            <custom-switch
                                v-model="referenceComponent.active"
                                @input="onBlurActive"
                            />
                            <span
                                class="text-xs font-semibold"
                                :class="
                                    referenceComponent.active
                                        ? 'text-theme-success'
                                        : 'text-theme-muted'
                                "
                            >
                                {{
                                    referenceComponent.active
                                        ? "Publicado"
                                        : "Oculto"
                                }}
                            </span>
                        </div>
                        <span
                            v-else
                            class="flex items-center gap-1 text-xs font-semibold"
                            :class="
                                referenceComponent.active
                                    ? 'text-theme-success'
                                    : 'text-theme-muted'
                            "
                        >
                            {{ referenceComponent.active ? "🟢" : "⚪" }}
                            {{
                                referenceComponent.active
                                    ? "Publicado"
                                    : "Oculto"
                            }}
                        </span>
                    </div>
                    <div
                        class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                    >
                        <span class="flex items-center gap-1">
                            <span
                                class="font-semibold uppercase tracking-wide text-theme-muted"
                            >
                                ID
                            </span>
                            <span class="font-medium text-theme-base">
                                {{ referenceComponent.id }}
                            </span>
                        </span>
                        <span
                            v-if="referenceComponent.barcode"
                            class="flex items-center gap-1 border-l border-theme-border pl-3"
                        >
                            <span
                                class="font-semibold uppercase tracking-wide text-theme-muted"
                            >
                                Cód
                            </span>
                            <span class="font-medium text-theme-base">
                                {{ referenceComponent.barcode }}
                            </span>
                        </span>
                        <span
                            v-if="defaultReferenceId === referenceComponent.id"
                            class="flex items-center gap-1 border-l border-theme-border pl-3 font-semibold text-theme-success"
                        >
                            ⭐&nbsp;Predeterminada
                        </span>

                        <span
                            v-if="sizeLabel"
                            class="hidden items-center gap-1 border-l border-theme-border pl-3 md:flex"
                        >
                            <span
                                class="font-semibold uppercase tracking-wide text-theme-muted"
                            >
                                {{ sizeLabel }}
                            </span>
                            <span class="font-medium text-theme-base">
                                {{ referenceComponent.size }}
                            </span>
                        </span>
                        <span
                            v-if="colorLabel"
                            class="hidden items-center gap-1 border-l border-theme-border pl-3 md:flex"
                        >
                            <span
                                class="font-semibold uppercase tracking-wide text-theme-muted"
                            >
                                {{ colorLabel }}
                            </span>
                            <span class="font-medium text-theme-base">
                                {{ referenceComponent.color }}
                            </span>
                            <span
                                class="h-3 w-3 rounded-full border border-theme-border"
                                :style="{
                                    'background-color':
                                        referenceComponent.colorRef
                                }"
                            ></span>
                        </span>
                        <span
                            v-if="showStock"
                            class="hidden items-center gap-1 border-l border-theme-border pl-3 md:flex"
                        >
                            <span
                                class="font-semibold uppercase tracking-wide text-theme-muted"
                            >
                                Stock
                            </span>
                            <span class="font-medium text-theme-base">
                                {{ referenceComponent.stock }}
                            </span>
                        </span>
                    </div>
                </div>
                <div class="flex items-center">
                    <dropdown-popover shadow border-thick>
                        <custom-avatar
                            pointer
                            size="large"
                            circle
                            class="bg-theme-primary text-theme-inverse"
                        >
                            <span> ⚙ </span>
                        </custom-avatar>
                        <template #tooltip>
                            <div
                                class="autocomplete-items list-group text-left w-full"
                            >
                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'reference:SyncStock'
                                        ) &&
                                        enabled &&
                                        referenceComponent.id
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a"
                                        :aria-label="
                                            referenceComponent.active
                                                ? 'Ocultar referencia'
                                                : 'Publicar referencia'
                                        "
                                        @click="togglePublishReferenceComponent"
                                    >
                                        {{
                                            referenceComponent.active
                                                ? "🙈&nbsp;Ocultar"
                                                : "📢&nbsp;Publicar"
                                        }}
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'reference:UpdateDefault'
                                        ) && !referenceComponent.isDefault
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a"
                                        aria-label="Poner por defecto"
                                        @click="setDefaultComponent"
                                    >
                                        🎭&nbsp;Poner por defecto
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'inventory:Get'
                                        ) &&
                                        selectedMerchant &&
                                        selectedMerchant.merchantId &&
                                        selectedMerchant.merchantType
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a whitespace-normal h-auto break-words"
                                        aria-label="Inventario en tienda"
                                        @click="goToInventory"
                                    >
                                        🧮&nbsp;Inventario en
                                        {{
                                            selectedMerchant.merchantType
                                                | merchantInfo("type")
                                        }}
                                        {{
                                            selectedMerchant.name
                                                | transformString("lower")
                                        }}
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'inventory:ListByReference'
                                        ) &&
                                        inventoryMode ===
                                            inventoryModes.merchant
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a"
                                        aria-label="Inventario global"
                                        @click="listInventoryReferenceComponent"
                                    >
                                        🧮&nbsp;Inventario global
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'inventory:Create'
                                        ) &&
                                        inventoryMode ===
                                            inventoryModes.merchant &&
                                        selectedMerchant &&
                                        selectedMerchant.merchantId &&
                                        selectedMerchant.merchantType
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a whitespace-normal h-auto break-words"
                                        aria-label="Ir a inicio de app"
                                        @click="openInventoryRecord"
                                    >
                                        🧮&nbsp;Registrar inventario
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission(
                                            'reference:SyncStock'
                                        ) &&
                                        inventoryMode ===
                                            inventoryModes.merchant
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a whitespace-normal h-auto break-words"
                                        aria-label="Sincronizar inventario"
                                        @click="syncStockReferenceComponent"
                                    >
                                        🔀&nbsp;Sincronizar inventario
                                    </button>
                                </div>

                                <div
                                    v-if="referenceComponent.active"
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a"
                                        aria-label="Ir a la referencia"
                                        @click="goToReference"
                                    >
                                        🚀&nbsp;Ver en página
                                    </button>
                                </div>

                                <div
                                    v-if="referenceComponent.barcode"
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a"
                                        aria-label="Códigoo de barras"
                                        @click="openBarcode"
                                    >
                                        📡&nbsp;Código de barras
                                    </button>
                                </div>

                                <div
                                    v-if="
                                        $ability.hasPermission('product:Delete')
                                    "
                                    class="md:m-2 m-1"
                                >
                                    <button
                                        class="font-bold btn-a-danger whitespace-normal h-auto break-words"
                                        aria-label="Eliminar referencia"
                                        @click="deleteReferenceComponent"
                                    >
                                        🗑️&nbsp;Eliminar referencia
                                    </button>
                                </div>
                            </div>
                        </template>
                    </dropdown-popover>
                    <app-button icon circle @click="sendActive">
                        <ChevronUpIcon
                            v-if="openReferenceId === referenceComponent.id"
                            class="w-5 h-5"
                        />
                        <ChevronDownIcon v-else class="w-5 h-5" />
                    </app-button>
                </div>
            </div>
        </div>

        <custom-collapse
            ref="container mx-auto"
            :visible="openReferenceId === referenceComponent.id"
        >
            <div class="py-4 space-y-6">
                <div>
                    <p
                        class="text-theme-muted text-xs font-semibold uppercase tracking-wider mb-3"
                    >
                        Variante
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <barcode-scanner-input
                            v-model="referenceComponent.barcode"
                            check
                            :enabled="enabled"
                            :reference-id="referenceComponent.id"
                            class="opacity-false"
                        />
                        <custom-input
                            v-if="showSize"
                            v-model="referenceComponent.size"
                            :label="sizeLabel"
                            :disabled="!enabled"
                        />
                        <color-input
                            v-if="showColor"
                            v-model="color"
                            :enabled="enabled"
                            :label="colorLabel"
                            class="opacity-false"
                        />
                        <div v-if="showCustomReference && customReferenceTitle">
                            <custom-input
                                v-model="
                                    referenceComponent.customReferenceValue
                                "
                                :label="customReferenceTitle"
                                class="w-full"
                                :disabled="!enabled"
                                @blur="trimCustomReferenceValue"
                            />
                        </div>
                    </div>
                </div>

                <div class="border-t border-brand-blue opacity-20"></div>

                <div>
                    <price-offer-fields
                        v-model="prices"
                        :enabled="enabled"
                        :show-cost="'cost' in referenceComponent"
                        :show-errors="introValidators.priceOffer"
                        @blur="onBlurPrices"
                        @error-change="pricesHaveError = $event"
                    />
                </div>

                <div v-if="$ability.hasPermission('reference:GetWholesale')">
                    <div class="border-t border-brand-blue opacity-20"></div>

                    <div>
                        <p
                            class="text-theme-muted text-xs font-semibold uppercase tracking-wider mt-6"
                        >
                            Precio al por mayor
                        </p>
                        <div class="mt-3">
                            <custom-checkbox
                                v-model="wholesaleActive"
                                :disabled="!enabled"
                                primary
                                @input="onToggleWholesale"
                            >
                                Activar precio al por mayor
                            </custom-checkbox>
                        </div>
                        <div
                            v-if="wholesaleActive"
                            class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4"
                        >
                            <input-money-component
                                v-model="referenceComponent.priceWholesale"
                                :enabled="enabled"
                                label="Precio al por mayor"
                                icon="💲"
                            />
                            <custom-input
                                v-model="
                                    referenceComponent.minUnitsForPriceWholesale
                                "
                                type="number"
                                min="0"
                                label="Unidades mínimas para precio al por mayor"
                                :disabled="!enabled"
                                :error="
                                    referenceComponent.minUnitsForPriceWholesale <
                                    0
                                        ? 'No puede ser un número negativo'
                                        : ''
                                "
                                @keydown.native="preventNegativeKey"
                            />
                        </div>
                    </div>
                </div>

                <div v-if="'alwaysStock' in referenceComponent">
                    <div
                        class="border-t border-brand-blue opacity-20 mb-6"
                    ></div>
                    <p
                        class="text-theme-muted text-xs font-semibold uppercase tracking-wider mb-3"
                    >
                        Inventario
                    </p>
                    <p
                        v-if="inventoryMode === inventoryModes.none"
                        class="text-sm text-theme-muted"
                    >
                        Tu negocio no maneja inventario: esta variante siempre
                        está disponible. Para llevar unidades, cambia el modo de
                        inventario en
                        <router-link
                            to="/app/settings/inventory"
                            class="text-theme-primary underline"
                        >
                            Configuración
                        </router-link>
                        .
                    </p>
                    <div v-else class="flex flex-col gap-4">
                        <checkbox-selector
                            v-model="referenceComponent.alwaysStock"
                            label="Siempre en inventario"
                            :enabled="enabled"
                        />
                        <div v-if="canEditStock" class="max-w-xs">
                            <custom-input
                                v-model.number="stockDraft"
                                type="number"
                                min="0"
                                label="Unidades en stock"
                                :error="stockError"
                                @keydown.native="preventNegativeKey"
                            />
                        </div>
                        <p
                            v-else-if="
                                inventoryMode === inventoryModes.merchant &&
                                showStock
                            "
                            class="text-sm text-theme-muted"
                        >
                            Stock: {{ referenceComponent.stock }} — es la suma
                            del inventario de tus sucursales y se edita en cada
                            sucursal.
                        </p>
                    </div>
                </div>

                <images-manage
                    :id="`${referenceComponent.id}`"
                    v-model="images"
                    class="mt-2"
                    :enabled="enabled"
                    message="producto"
                    :default-alt="'Imagen de ' + product.name"
                    default-size="squareImage"
                    :default-filters="{ size: 'squareImage' }"
                />

                <div v-if="enabled" class="w-full flex justify-center pt-2">
                    <app-button
                        v-if="!referenceComponent.id"
                        size="large"
                        :loading="isLoading"
                        @click="createReferenceComponent"
                    >
                        Crear referencia
                    </app-button>
                    <fragment v-else>
                        <app-button
                            size="large"
                            primary
                            :loading="isLoading"
                            @click="updateReferenceComponent"
                        >
                            Actualizar referencia
                        </app-button>
                        <app-button
                            v-if="referencesCount >= 2"
                            size="large"
                            success
                            :loading="isLoading"
                            @click="openUpdateMultipleReferenceModal"
                        >
                            Actualización múltiple
                        </app-button>
                    </fragment>
                </div>
            </div>
        </custom-collapse>

        <app-dialog v-model="isOpenModalMultipleUpdate" size="lg">
            <template #header>
                <div class="flex mt-2">
                    <div
                        class="w-10/12 mx-auto text-center flex flex-col items-center"
                    >
                        <span class="w-full h4">
                            Actualización múltiple de referencias
                        </span>
                    </div>
                </div>
            </template>
            <div>
                <span class="w-full block">
                    Aplicar configuraciones de referencia (Cod.
                    {{ referenceComponent.barcode }}
                    ID. {{ referenceComponent.id }}) a todas las referecias del
                    producto (SKU {{ referenceComponent.sku }} ID.
                    {{ referenceComponent.productId }})
                </span>

                <div class="flex flex-col gap-3 mt-4">
                    <custom-checkbox
                        v-model="multipleUpdateOptions"
                        val="prices"
                    >
                        Precios: Precio de venta
                        {{ referenceComponent.priceOffer | money }}
                        <template
                            v-if="
                                isOfferPrice(
                                    referenceComponent.price,
                                    referenceComponent.priceOffer
                                )
                            "
                        >
                            · Precio anterior
                            {{ referenceComponent.price | money }}
                        </template>
                        · Costo {{ referenceComponent.cost | money }}
                    </custom-checkbox>

                    <custom-checkbox
                        v-if="$ability.hasPermission('reference:GetWholesale')"
                        v-model="multipleUpdateOptions"
                        val="forWholesale"
                    >
                        Opciones precio al por mayor: Unidades
                        {{ referenceComponent.minUnitsForPriceWholesale }} |
                        Precio
                        {{ referenceComponent.priceWholesale | money }}
                    </custom-checkbox>

                    <custom-checkbox
                        v-model="multipleUpdateOptions"
                        val="color"
                    >
                        {{ colorLabel }}:
                        {{
                            referenceComponent.color
                                | transformString("capitalize")
                        }}
                    </custom-checkbox>

                    <custom-checkbox v-model="multipleUpdateOptions" val="size">
                        {{ sizeLabel }}:
                        {{ referenceComponent.size }}
                    </custom-checkbox>

                    <custom-checkbox
                        v-model="multipleUpdateOptions"
                        val="images"
                    >
                        Imagenes: # {{ images.length }}
                    </custom-checkbox>

                    <custom-checkbox
                        v-if="inventoryMode !== inventoryModes.none"
                        v-model="multipleUpdateOptions"
                        val="alwaysStock"
                    >
                        Siempre en inventario:
                        {{
                            referenceComponent.alwaysStock
                                ? " Activado"
                                : " Desactivado"
                        }}
                    </custom-checkbox>
                </div>

                <div class="flex justify-center mt-6">
                    <app-button
                        size="large"
                        @click="updateMultipleReferenceComponent"
                    >
                        Actualizar referencias
                    </app-button>
                </div>
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenModalInventory">
            <template #header>
                <div class="flex mt-2">
                    <div
                        class="w-10/12 mx-auto text-center flex flex-col items-center"
                    >
                        <span class="w-full text-lg">
                            Registro de inventario para la tienda
                        </span>
                        <strong class="text-theme-primary text-lg">
                            {{ selectedMerchant.name }}
                        </strong>
                    </div>
                </div>
            </template>
            <div>
                <inventory-by-reference
                    :price-offer="referenceComponent.priceOffer"
                    :cost-initial="referenceComponent.cost"
                    @create="recordInventory"
                />
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenModalGetInventory">
            <template #header>
                <div class="flex flex-col mx-3 my-4">
                    <span class="text-lg">
                        Inventario para referencia
                        <span class="text-theme-primary">
                            # {{ referenceComponent.id }}
                        </span>
                    </span>
                    <small>
                        Color {{ referenceComponent.color }} | Tamaño
                        {{ referenceComponent.size }}
                    </small>
                </div>
            </template>
            <div>
                <inventory-by-reference-table
                    :inventories="inventories"
                    :stock-active="stockActive"
                />
            </div>
        </app-dialog>

        <app-dialog v-model="isOpenBarcode">
            <template #header>
                <div class="flex mt-2 w-full">
                    <div
                        class="w-10/12 mx-auto text-center flex flex-col items-center"
                    >
                        <span class="w-full font-bold h4">
                            Código de barras
                        </span>
                    </div>
                </div>
            </template>
            <div class="flex justify-center">
                <barcode-generator :barcode="referenceComponent.barcode">
                    <template #top>
                        <div
                            class="flex flex-wrap items-center justify-center gap-1 mb-1"
                        >
                            <span class="text-black">
                                {{ product.name | cut(38) }}
                            </span>
                            <span
                                v-if="referenceComponent.size !== 'unico'"
                                class="text-black"
                            >
                                - {{ referenceComponent.size }}
                            </span>
                        </div>
                    </template>
                    <template #bottom>
                        <span class="text-lg text-black">
                            {{ referenceComponent.barcode }}
                        </span>
                    </template>
                </barcode-generator>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import { INVENTORY_MODES } from "@/common/fields/inventory-modes";
import { productTypesReferenceConfig } from "@/common/fields/product-types";
import { preventNegativeKey } from "@/common/lib/number-input";
import { isOfferPrice } from "@/common/lib/price-offer";

import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import ChevronUpIcon from "@/components/icons/ChevronUpIcon.vue";
import InventoryByReference from "@/components/inventories/InventoryByReference.vue";
import InventoryByReferenceTable from "@/components/inventories/InventoryByReferenceTable.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import BarcodeGenerator from "@/components/utils/BarcodeGenerator.vue";
import BarcodeScannerInput from "@/components/utils/BarcodeScannerInput.vue";
import CheckboxSelector from "@/components/utils/CheckboxSelector.vue";
import ColorInput from "@/components/utils/ColorInput.vue";
import CustomCollapse from "@/components/utils/CustomCollapse.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import PriceOfferFields from "@/components/utils/PriceOfferFields.vue";

export default {
    name: "ReferenceManage",
    components: {
        AppDialog,
        CustomCollapse,
        ColorInput,
        ImagesManage,
        BarcodeGenerator,
        CheckboxSelector,
        BarcodeScannerInput,
        InputMoneyComponent,
        PriceOfferFields,
        InventoryByReference,
        InventoryByReferenceTable,
        CustomInput,
        ChevronDownIcon,
        ChevronUpIcon
    },
    props: {
        product: {
            type: Object,
            default: () => ({
                name: "Cargando..",
                type: "article"
            }),
            required: false
        },
        reference: {
            default: () => ({
                active: true,
                minUnitsForPriceWholesale: 2,
                priceWholesale: 0,
                color: "blanco",
                colorRef: "#fff",
                createdAt: "2021-10-26T00:27:59.146Z",
                isDefault: true,
                id: 21,
                alwaysStock: false,
                barcode: "",
                images: [],
                cost: 38000,
                price: 50000,
                priceOffer: 40000,
                productId: 17,
                size: "unico",
                stock: 20,
                updatedAt: "2021-10-26T00:27:59.146Z"
            }),
            type: Object,
            required: true
        },
        openReferenceId: {
            type: Number,
            default: 0,
            required: false
        },
        defaultReferenceId: {
            type: Number,
            default: 0,
            required: false
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        customReferenceTitle: {
            type: String,
            default: null,
            required: false
        },
        referencesCount: {
            type: Number,
            default: 1,
            required: false
        },
        // sucursales-inventario-multitenant: `inventoryMode` del tenant
        // (`GET /bo/settings/inventory`); null si no se pudo leer.
        inventoryMode: {
            type: String,
            default: null,
            required: false
        }
    },
    emits: [
        "remove",
        "created",
        "update",
        "changeActive",
        "goToReference",
        "setDefault"
    ],
    data: () => ({
        stockActive: 0,
        stockDraft: 0,
        stockError: "",
        isLoading: false,
        hasGlobalScope: false,
        // Sin `priceWholesale` el precio al por mayor está apagado.
        wholesaleActive: false,
        isOpenBarcode: false,
        isLoadingUpdateMultipleReference: false,
        isOpenModalInventory: false,
        isLoadingGetInventory: false,
        loadingCreateInventory: false,
        showCustomReference: false,
        isLoadingSyncStock: false,
        isLoadingUpdateReference: false,
        isLoadingCreateReference: false,
        isOpenModalGetInventory: false,
        isOpenModalMultipleUpdate: false,
        showSize: true,
        showColor: true,
        sizeLabel: "Tamaño/Talla/Valor",
        colorLabel: "Color",
        multipleUpdateOptions: [],
        inventories: [],
        introValidators: {
            stock: false,
            priceOffer: false
        },
        pricesHaveError: false,
        images: [],
        color: {
            color: "blanco",
            colorRef: "#fff"
        },
        referenceComponent: {
            id: 0,
            active: true,
            minUnitsForPriceWholesale: 1,
            stock: 20,
            barcode: "",
            color: "blanco",
            colorRef: "#fff",
            price: 0,
            priceOffer: 0,
            priceWholesale: 0,
            cost: 0,
            productId: 17,
            size: "unico",
            isDefault: true,
            createdAt: "2021-10-26T00:27:59.146Z",
            updatedAt: "2021-10-26T00:27:59.146Z",
            images: []
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapGetters("control", ["backgroundColor"]),
        inventoryModes() {
            return INVENTORY_MODES;
        },
        // Con `alwaysStock` el sistema asume unidades
        // (`STOCK_FOR_ALWAYS_STOCK` del backend): el número guardado no
        // aplica, así que ni se muestra ni se edita.
        showStock() {
            return (
                this.referenceComponent.stock !== undefined &&
                !this.referenceComponent.alwaysStock
            );
        },
        canEditStock() {
            return (
                this.showStock &&
                this.inventoryMode === INVENTORY_MODES.global &&
                this.enabled &&
                Boolean(this.referenceComponent.id) &&
                this.$ability.hasPermission("reference:UpdateStock")
            );
        },
        prices: {
            get() {
                return {
                    price: this.referenceComponent.price,
                    priceOffer: this.referenceComponent.priceOffer,
                    cost: this.referenceComponent.cost
                };
            },
            set({ cost, price, priceOffer }) {
                this.referenceComponent.price = price;
                this.referenceComponent.priceOffer = priceOffer;
                if ("cost" in this.referenceComponent) {
                    this.referenceComponent.cost = cost;
                }
            }
        }
    },
    mounted() {
        this.images = this.reference.images;
        this.color = {
            color: this.reference.color,
            colorRef: this.reference.colorRef
        };
        this.referenceComponent = this.reference;
        this.stockDraft = this.reference.stock || 0;
        this.wholesaleActive =
            this.reference.priceWholesale !== null &&
            this.reference.priceWholesale !== undefined;
        const result = productTypesReferenceConfig[this.product.type];
        if (result) {
            this.showSize = result.showSize;
            this.showColor = result.showColor;
            this.showCustomReference =
                result.showCustomReference || !!this.customReferenceTitle;
            this.sizeLabel = result.sizeLabel;
            this.colorLabel = result.colorLabel;
        }
    },
    methods: {
        preventNegativeKey,
        isOfferPrice,
        ...mapActions("products", [
            "createReference",
            "updateReference",
            "updateMultipleReferences",
            "syncStockReference",
            "deleteReference"
        ]),
        ...mapActions("inventories", [
            "createInventory",
            "listInventoryByReference"
        ]),
        trimCustomReferenceValue() {
            const val = this.referenceComponent.customReferenceValue;
            this.referenceComponent.customReferenceValue = val
                ? val.trim() || null
                : null;
        },
        openUpdateMultipleReferenceModal() {
            if (!this.validateReference()) {
                return;
            }
            this.isOpenModalMultipleUpdate = true;
        },
        async listInventoryReferenceComponent() {
            this.isLoading = true;
            this.isOpenModalGetInventory = true;
            this.isLoadingGetInventory = true;
            try {
                const { items, stock } = await this.listInventoryByReference({
                    id: this.referenceComponent.id
                });
                this.inventories = items;
                this.stockActive = stock;
                this.referenceComponent.stock = stock;
                this.isLoading = false;
                this.isLoadingGetInventory = false;
            } catch (error) {
                this.isLoading = false;
                this.isLoadingGetInventory = false;
                this.isOpenModalGetInventory = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async createReferenceComponent() {
            if (!this.validateReference()) {
                return;
            }
            this.isLoading = true;
            try {
                const info = {
                    ...this.referenceComponent,
                    ...this.wholesalePayload(),
                    stock: 0,
                    colorRef: this.color.colorRef,
                    color: this.color.color,
                    images: this.images.map((item) => ({
                        id: item.id,
                        sequence: item.sequence.sequence
                    }))
                };
                const resultReference = await this.createReference(info);
                this.referenceComponent.id = resultReference.id;
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Creación exitosa",
                    text: `Id: ${this.referenceComponent.id}`,
                    icon: "success",
                    showCloseButton: true
                });
                this.$emit("created", info);
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        // Encendido arranca con el precio de venta y 1 unidad mínima.
        onToggleWholesale(active) {
            if (!active) return;
            if (!this.referenceComponent.priceWholesale) {
                this.referenceComponent.priceWholesale =
                    this.referenceComponent.priceOffer;
            }
            if (!this.referenceComponent.minUnitsForPriceWholesale) {
                this.referenceComponent.minUnitsForPriceWholesale = 1;
            }
        },
        // Apagado: `priceWholesale: null` y sin mínimo (el backend lo
        // entiende como sin precio al por mayor). Sin `reference:UpdateWholesale`
        // van en `undefined` para que no viajen en el body (pisan lo que trae
        // `referenceComponent`): el backend responde 403 si el body los trae,
        // aunque sean `null`.
        wholesalePayload() {
            if (!this.$ability.hasPermission("reference:UpdateWholesale")) {
                return {
                    priceWholesale: undefined,
                    minUnitsForPriceWholesale: undefined
                };
            }
            if (!this.wholesaleActive) {
                return {
                    priceWholesale: null,
                    minUnitsForPriceWholesale: undefined
                };
            }
            return {
                priceWholesale: this.referenceComponent.priceWholesale,
                minUnitsForPriceWholesale:
                    this.referenceComponent.minUnitsForPriceWholesale || 1
            };
        },
        async toggleActiveReferenceComponent() {
            this.isLoading = true;
            try {
                await this.updateReference({
                    active: this.referenceComponent.active,
                    id: this.referenceComponent.id
                });
                this.$emit("update", this.referenceComponent);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },

        async updateMultipleReferenceComponent() {
            if (!this.validateReference()) {
                return;
            }
            this.isLoading = true;
            try {
                const info = {
                    id: this.referenceComponent.productId,
                    ...{
                        ...(this.multipleUpdateOptions.includes("prices")
                            ? {
                                  price: this.referenceComponent.price,
                                  priceOffer:
                                      this.referenceComponent.priceOffer,
                                  cost: this.referenceComponent.cost
                              }
                            : null)
                    },
                    ...{
                        ...(this.multipleUpdateOptions.includes("color")
                            ? {
                                  colorRef: this.color.colorRef,
                                  color: this.color.color
                              }
                            : null)
                    },
                    ...{
                        ...(this.multipleUpdateOptions.includes("forWholesale")
                            ? {
                                  priceWholesale:
                                      this.referenceComponent.priceWholesale,
                                  minUnitsForPriceWholesale:
                                      this.referenceComponent
                                          .minUnitsForPriceWholesale
                              }
                            : null)
                    },
                    ...{
                        ...(this.multipleUpdateOptions.includes("size")
                            ? {
                                  size: this.referenceComponent.size
                              }
                            : null)
                    },
                    ...{
                        ...(this.multipleUpdateOptions.includes("alwaysStock")
                            ? {
                                  alwaysStock:
                                      this.referenceComponent.alwaysStock
                              }
                            : null)
                    },
                    ...{
                        ...(this.multipleUpdateOptions.includes("images")
                            ? {
                                  images: this.images.map((item) => ({
                                      id: item.id,
                                      sequence: item.sequence.sequence
                                  }))
                              }
                            : null)
                    }
                };
                await this.updateMultipleReferences(info);
                this.$emit("updates", info);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
            } catch (error) {
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            } finally {
                this.isLoading = false;
                this.isLoadingUpdateMultipleReference = false;
                this.isOpenModalMultipleUpdate = false;
            }
        },
        async updateReferenceComponent() {
            if (!this.validateReference() || !this.validateStockDraft()) {
                return;
            }
            this.isLoading = true;
            try {
                const info = {
                    ...this.referenceComponent,
                    ...this.wholesalePayload(),
                    // `stock` solo viaja si se puede editar (modo global +
                    // `reference:UpdateStock`): si no, el backend responde
                    // 403/409 aunque sea el mismo valor que ya trae.
                    stock: this.canEditStock ? this.stockDraft : undefined,
                    colorRef: this.color.colorRef,
                    color: this.color.color,
                    images: this.images.map((item) => ({
                        id: item.id,
                        sequence: item.sequence.sequence
                    }))
                };
                await this.updateReference(info);
                if (info.stock !== undefined) {
                    this.referenceComponent.stock = info.stock;
                }
                this.$emit("update", info);
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteReferenceComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Seguro ?",
                text: "Recuerda se va a eliminar la referencia y la relación de imágenes",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.$emit("remove", this.referenceComponent);
            }
        },
        async syncStockReferenceComponent() {
            this.isLoading = true;
            try {
                const result = await this.syncStockReference(
                    this.referenceComponent
                );
                this.referenceComponent.stock = result.stock;
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        validateStockDraft() {
            if (!this.canEditStock) return true;
            const stock = this.stockDraft;
            if (!Number.isInteger(stock) || stock < 0) {
                this.stockError = "Escribe un número entero de 0 o más";
                return false;
            }
            this.stockError = "";
            return true;
        },
        async setDefaultComponent() {
            setTimeout(() => {
                this.$emit("setDefault", this.referenceComponent.id);
            }, 100);
        },
        async recordInventory({ costNew, quantity }) {
            this.loadingCreateInventory = true;
            try {
                await this.createInventory({
                    merchantType: this.selectedMerchant.merchantType,
                    merchantId: this.selectedMerchant.merchantId,
                    referenceId: this.referenceComponent.id,
                    costNew,
                    quantity
                });
                this.isOpenModalInventory = false;
                this.$sound.play();
            } catch (error) {
                this.isOpenModalInventory = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                this.isOpenModalInventory = true;
            } finally {
                this.loadingCreateInventory = false;
            }
        },
        goToInventory() {
            window
                .open(
                    `/app/inventories/${this.selectedMerchant.merchantType}/${this.selectedMerchant.merchantId}/detail/${this.referenceComponent.id}`,
                    "_blank"
                )
                .focus();
        },
        goToReference() {
            this.$emit(
                "goToReference",
                `?reference=${this.referenceComponent.id}`
            );
        },
        sendActive() {
            if (this.referenceComponent.id === this.openReferenceId) {
                this.$emit("changeActive", 0);
            } else {
                this.$emit("changeActive", this.referenceComponent.id);
            }
        },
        openInventoryRecord() {
            this.isOpenModalInventory = true;
        },
        validateReference() {
            this.introValidators = {
                ...this.introValidators,
                priceOffer: true,
                stock: true
            };
            if (
                this.color.color === "" ||
                this.color.colorRef === "" ||
                this.referenceComponent.size === "" ||
                !this.referenceComponent.priceOffer ||
                !this.referenceComponent.price ||
                this.pricesHaveError
            ) {
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Variante invalida",
                    text: "Revisa por favor que todo este completo",
                    icon: "info"
                });
                return false;
            }
            return true;
        },
        openBarcode() {
            this.isOpenBarcode = true;
        },
        onBlurPrices() {
            this.introValidators.priceOffer = true;
            this.$emit("update", this.referenceComponent);
        },
        onBlurStock() {
            this.introValidators.stock = true;
            this.$emit("update", this.referenceComponent);
        },
        onBlurActive() {
            this.toggleActiveReferenceComponent();
        },
        togglePublishReferenceComponent() {
            this.referenceComponent.active = !this.referenceComponent.active;
            this.toggleActiveReferenceComponent();
        }
    }
};
</script>
